import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  AlertTriangle,
  CalendarClock,
  Layers3,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { inventoryService, warehousesService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InventoryItem, ItemBatch, ItemBatchStatus } from '@/types/inventory';

interface Props {
  item: InventoryItem | null;
}

const STATUS_TONE: Record<ItemBatchStatus, string> = {
  ACTIVE: 'bg-success/10 text-success border-success/20',
  DEPLETED: 'bg-muted/40 text-muted-foreground border-border/60',
  EXPIRED: 'bg-warning/10 text-[hsl(38_85%_30%)] border-warning/30',
  RECALLED: 'bg-destructive/10 text-destructive border-destructive/30',
};

/** Days from today; negative = already expired. */
function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function ItemBatchesTab({ item }: Props) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const itemId = item?.id ?? null;

  const batchesQuery = useQuery({
    queryKey: ['inventory', 'batches', itemId],
    queryFn: () => inventoryService.listBatches(itemId!),
    enabled: !!itemId,
  });

  const warehousesQuery = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: !!itemId,
    staleTime: 60_000,
  });

  const removeMutation = useMutation({
    mutationFn: (batchId: string) => inventoryService.deleteBatch(itemId!, batchId),
    onSuccess: () => {
      toast.success('Batch deleted');
      qc.invalidateQueries({ queryKey: ['inventory', 'batches', itemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete batch'),
  });

  const setStatusMutation = useMutation({
    mutationFn: ({ batchId, status }: { batchId: string; status: ItemBatchStatus }) =>
      inventoryService.updateBatch(itemId!, batchId, { status }),
    onSuccess: () => {
      toast.success('Batch updated');
      qc.invalidateQueries({ queryKey: ['inventory', 'batches', itemId] });
    },
  });

  if (!item) return null;

  if (!item.tracksBatches) {
    return (
      <div className="rounded-md border border-info/20 bg-info/5 px-3 py-3 text-[12.5px]">
        <span className="font-medium">Batch tracking is off for this item.</span>{' '}
        <span className="text-muted-foreground">
          Edit the item and enable "Track batches / lots" to record per-batch
          mfg/expiry dates and use FIFO picking.
        </span>
      </div>
    );
  }

  const batches = batchesQuery.data ?? [];
  const warehouses = warehousesQuery.data ?? [];

  const expiringSoon = batches.filter((b) => {
    const d = daysUntil(b.expiryDate);
    return d !== null && d >= 0 && d <= 30 && b.status === 'ACTIVE';
  });
  const expired = batches.filter((b) => {
    const d = daysUntil(b.expiryDate);
    return d !== null && d < 0;
  });

  return (
    <div className="space-y-4">
      {/* Quick alerts */}
      {(expiringSoon.length > 0 || expired.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {expired.length > 0 && (
            <div className="flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1 text-[11.5px] text-destructive">
              <AlertTriangle className="h-3 w-3" />
              <span className="font-medium">
                {expired.length} expired batch{expired.length === 1 ? '' : 'es'}
              </span>
            </div>
          )}
          {expiringSoon.length > 0 && (
            <div className="flex items-center gap-1.5 rounded-md border border-warning/30 bg-warning/5 px-2.5 py-1 text-[11.5px] text-[hsl(38_85%_30%)]">
              <CalendarClock className="h-3 w-3" />
              <span className="font-medium">
                {expiringSoon.length} expiring within 30 days
              </span>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          <Layers3 className="mr-1 inline h-3 w-3" />
          Batches
          <span className="ml-2 font-mono text-foreground">{batches.length}</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
          <Plus className="h-3 w-3" />
          Add batch
        </Button>
      </div>

      {creating && (
        <CreateBatchForm
          itemId={item.id}
          warehouses={warehouses.map((w) => ({ id: w.id, code: w.code, name: w.name }))}
          onClose={() => setCreating(false)}
        />
      )}

      {batchesQuery.isLoading ? (
        <div className="text-[12px] text-muted-foreground">Loading batches…</div>
      ) : batches.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/70 px-3 py-6 text-center text-[12.5px] text-muted-foreground">
          No batches yet. Add one to start tracking mfg/expiry per lot.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border/60">
          <div className="grid grid-cols-12 gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            <div className="col-span-3">Batch #</div>
            <div className="col-span-2">Mfg</div>
            <div className="col-span-2">Expiry</div>
            <div className="col-span-2 text-right">On hand</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1" />
          </div>
          {batches.map((b) => (
            <BatchRow
              key={b.id}
              batch={b}
              onDelete={() =>
                removeMutation.mutate(b.id, {
                  onSuccess: undefined, // already configured at the mutation
                })
              }
              onSetStatus={(status) => setStatusMutation.mutate({ batchId: b.id, status })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BatchRow({
  batch,
  onDelete,
  onSetStatus,
}: {
  batch: ItemBatch;
  onDelete: () => void;
  onSetStatus: (status: ItemBatchStatus) => void;
}) {
  const expiryDays = daysUntil(batch.expiryDate);
  const expirySoon = expiryDays !== null && expiryDays >= 0 && expiryDays <= 30;
  const expired = expiryDays !== null && expiryDays < 0;

  return (
    <div className="grid grid-cols-12 items-center gap-2 border-b border-border/60 px-3 py-2 text-[12.5px] last:border-0">
      <div className="col-span-3 min-w-0">
        <div className="truncate font-mono text-[12px] text-foreground">{batch.batchNumber}</div>
        {batch.supplierRef && (
          <div className="truncate text-[10.5px] text-muted-foreground">
            ref {batch.supplierRef}
          </div>
        )}
      </div>
      <div className="col-span-2 text-[12px] text-muted-foreground">
        {batch.mfgDate ? fmt.date(batch.mfgDate) : '—'}
      </div>
      <div className="col-span-2 text-[12px]">
        {batch.expiryDate ? (
          <div>
            <div
              className={cn(
                'tabular-nums',
                expired && 'text-destructive font-medium',
                expirySoon && !expired && 'text-[hsl(38_85%_30%)]',
                !expired && !expirySoon && 'text-muted-foreground',
              )}
            >
              {fmt.date(batch.expiryDate)}
            </div>
            <div className="text-[10.5px] text-muted-foreground">
              {expired
                ? `${Math.abs(expiryDays!)} d ago`
                : `in ${expiryDays} d`}
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
      <div className="col-span-2 text-right">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help font-mono text-[12.5px] tabular-nums text-foreground">
              {fmt.number(batch.totalOnHand)}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5 text-[11px]">
              {batch.warehouseBreakdown.length === 0 ? (
                <div className="text-muted-foreground">No stock</div>
              ) : (
                batch.warehouseBreakdown.map((w) => (
                  <div key={w.warehouseId} className="flex items-center gap-3">
                    <span className="font-mono">{w.warehouseCode}</span>
                    <span className="font-mono tabular-nums">
                      {fmt.number(w.quantityOnHand)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="col-span-2">
        <Select
          value={batch.status}
          onValueChange={(v) => onSetStatus(v as ItemBatchStatus)}
        >
          <SelectTrigger
            className={cn('h-7 border px-2 text-[11px]', STATUS_TONE[batch.status])}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="RECALLED">Recalled</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
            <SelectItem value="DEPLETED">Depleted</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-1 flex items-center justify-end">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onDelete}
              disabled={batch.totalOnHand > 0}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {batch.totalOnHand > 0
              ? 'Adjust qty to zero before deleting'
              : 'Delete batch'}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function CreateBatchForm({
  itemId,
  warehouses,
  onClose,
}: {
  itemId: string;
  warehouses: Array<{ id: string; code: string; name: string }>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [batchNumber, setBatchNumber] = useState('');
  const [mfgDate, setMfgDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [openingWarehouseId, setOpeningWarehouseId] = useState<string | undefined>(
    warehouses[0]?.id,
  );
  const [openingQuantity, setOpeningQuantity] = useState<string>('');

  const create = useMutation({
    mutationFn: () =>
      inventoryService.createBatch(itemId, {
        batchNumber: batchNumber.trim(),
        mfgDate: mfgDate || undefined,
        expiryDate: expiryDate || undefined,
        supplierRef: supplierRef.trim() || undefined,
        openingWarehouseId: openingQuantity ? openingWarehouseId : undefined,
        openingQuantity: openingQuantity ? Number(openingQuantity) : undefined,
      }),
    onSuccess: () => {
      toast.success('Batch created');
      qc.invalidateQueries({ queryKey: ['inventory', 'batches', itemId] });
      qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'movements'] });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create batch'),
  });

  const valid =
    batchNumber.trim().length > 0 &&
    (!openingQuantity || (Number(openingQuantity) > 0 && !!openingWarehouseId));

  return (
    <div className="rounded-md border border-border/70 bg-card p-3.5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          New batch
        </div>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-4">
          <label className="text-[11px] text-muted-foreground" htmlFor="batchNumber">
            Batch #
          </label>
          <Input
            id="batchNumber"
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder="LOT-2026-04A"
            className="mt-1 h-8 font-mono text-[12.5px]"
          />
        </div>
        <div className="col-span-3">
          <label className="text-[11px] text-muted-foreground" htmlFor="mfgDate">
            Mfg date
          </label>
          <Input
            id="mfgDate"
            type="date"
            value={mfgDate}
            onChange={(e) => setMfgDate(e.target.value)}
            className="mt-1 h-8 text-[12.5px]"
          />
        </div>
        <div className="col-span-3">
          <label className="text-[11px] text-muted-foreground" htmlFor="expiryDate">
            Expiry date
          </label>
          <Input
            id="expiryDate"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            className="mt-1 h-8 text-[12.5px]"
          />
        </div>
        <div className="col-span-2">
          <label className="text-[11px] text-muted-foreground" htmlFor="supplierRef">
            Supplier ref
          </label>
          <Input
            id="supplierRef"
            value={supplierRef}
            onChange={(e) => setSupplierRef(e.target.value)}
            placeholder="GRN-1024"
            className="mt-1 h-8 text-[12.5px]"
          />
        </div>

        <div className="col-span-12 mt-1 border-t border-border/60 pt-3">
          <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Opening stock (optional — creates an INBOUND movement)
          </div>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-7">
              <label className="text-[11px] text-muted-foreground" htmlFor="openingWh">
                Warehouse
              </label>
              <Select
                value={openingWarehouseId}
                onValueChange={(v) => setOpeningWarehouseId(v)}
              >
                <SelectTrigger id="openingWh" className="mt-1 h-8 text-[12.5px]">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      <span className="font-mono text-[10.5px] text-muted-foreground">{w.code}</span>
                      <span className="ml-2">{w.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-5">
              <label className="text-[11px] text-muted-foreground" htmlFor="openingQty">
                Quantity
              </label>
              <Input
                id="openingQty"
                type="number"
                min="0"
                step="0.001"
                value={openingQuantity}
                onChange={(e) => setOpeningQuantity(e.target.value)}
                placeholder="0"
                className="mt-1 h-8 text-right font-mono text-[12.5px] tabular-nums"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => create.mutate()}
          disabled={!valid || create.isPending}
        >
          {create.isPending ? 'Creating…' : 'Create batch'}
        </Button>
      </div>
    </div>
  );
}
